import {Avatar, Button, Form, Input, Radio, Spin} from 'antd'
import {docs_cancelCodiDocumentRow_Api} from 'apis'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useState} from 'react'
import {BsTrash} from 'react-icons/bs'
import {HiCode} from 'react-icons/hi'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {NullAsDefault, renderIf} from 'views/Shared'
import {CodiAdjModes, useRowDetails} from '../CodiDocumentContext'
import AdjPicker from '../components/Codi/AdjPicker'
import {renderAdjRow} from '../functions/renderAdjRow'
import {LoadingPlaceholder} from '../functions/renderLoading'

const FormItem = ({ label, ...props }) => (
  <Form.Item
    label={
      label ? (
        <Translate>
          {(t) => (
            <span className="capitalize text-sm font-semibold">{t(label)}</span>
          )}
        </Translate>
      ) : undefined
    }
    style={{
      marginBottom: 10,
    }}
    {...props}
  />
)

const CancelCodiContent = ({
  id,
  params,
  Wrapper = 'div',
  onCancel = NullAsDefault,
  onSuccess = NullAsDefault,
  renderActions = NullAsDefault,
}) => {
  const t = useTranslate()

  const [form] = Form.useForm()

  const row_id = params?.id

  const [isSubmitting, setSubmitting] = useState(false)

  const default_mode = CodiAdjModes.USE_REFERENCES.value

  const [form_values, set_form_values] = useState({
    adj_mode: default_mode,
  })

  const { item, isLoading = false } = useRowDetails(row_id)

  const { handleAsyncAction = NullAsDefault } = useAsyncAction({
    apiInfo: docs_cancelCodiDocumentRow_Api,
    query: bindQueryParams([{ id }, { row_id }]),
    onSuccess: () => {
      successNotify(NotificationActionTypes.INFORMATION, t)
      setSubmitting(false)
      onSuccess()
    },
    onError: (e, o) => {
      setSubmitting(false)
      notifyOnError(t)(e, o)
    },
  })

  const handleSubmit = async (values) => {
    await form.validateFields()
    setSubmitting(true)
    const { adj_url, adj_note, adj_document_row_id } = values ?? {}
    handleAsyncAction(
      _.omitBy(
        {
          adj_url,
          adj_note,
          adj_document_row_id,
        },
        _.isUndefined
      )
    )
  }

  const rules = [{ required: true, message: t('required field') }]

  return (
    <Form
      key={item}
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={handleSubmit}
      className="flex flex-col flex-1">
      <Wrapper className="flex flex-col flex-1">
        <div className="flex flex-col mx-auto max-w-2xl w-full gap-3 py-2 px-8 custom-form-codi">
          <div className="flex justify-center font-bold text-color-000 text-xl uppercase">
            {t('reject content')}
          </div>
          <LoadingPlaceholder isLoading={isLoading}>
            <div className="flex items-center gap-3 mt-3 mb-2">
              <Avatar
                size={40}
                icon={<BsTrash className="text-orange-600" />}
                className="flex flex-center bg-orange-100"
              />
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-orange-600">
                  {t('Do you want to remove this content')}
                </span>
                <span className="text-xs italic font-light text-color-500">
                  {t('remove content description')}
                </span>
              </div>
            </div>

            <div className="text-sm space-x-2">
              <span className="text-color-000 font-bold">
                {_.get(item, 'short_index_name')} {':'}
              </span>
              {Boolean(_.get(item, 'is_html')) ? (
                <div className="flex flex-col flex-center gap-1 py-3 background-100 rounded-lg mt-2">
                  <HiCode
                    size={30}
                    className="text-color-400"
                  />
                  <span className="text-xs text-color-500 italic">
                    {t('here is the HTML code')}
                  </span>
                </div>
              ) : (
                <span className="text-color-100 font-medium">
                  {_.get(item, 'content')}
                </span>
              )}
            </div>
          </LoadingPlaceholder>

          <div className="flex flex-col gap-1">
            <span className="font-medium text-color-300 text-sm uppercase">
              {t('based on')}
            </span>
            <Spin spinning={!!isLoading}>
              <div className="flex flex-col mb-3">
                <React.Fragment>
                  <Radio.Group
                    name="adj_mode"
                    className="my-1"
                    defaultValue={default_mode}
                    onChange={(event) => {
                      const { name, value } = event.target
                      set_form_values({
                        ...(form_values ?? {}),
                        [name]: value,
                      })
                    }}>
                    {[CodiAdjModes.USE_REFERENCES, CodiAdjModes.USE_NOTE].map(
                      ({ label, value }, index) => (
                        <Radio
                          key={index}
                          value={value}>
                          {t(label)}
                        </Radio>
                      )
                    )}
                  </Radio.Group>
                  {form_values?.adj_mode === CodiAdjModes.USE_NOTE.value ? (
                    <div className="flex flex-col">
                      <FormItem
                        label="note"
                        name="adj_note"
                        rules={rules}>
                        <Input.TextArea
                          rows={3}
                          className="flex-1"
                          placeholder={t('note')}
                        />
                      </FormItem>
                      <FormItem
                        label="url"
                        name="adj_url"
                        rules={rules}>
                        <Input
                          type="url"
                          inputMode="url"
                          className="flex-1"
                          placeholder={t('url')}
                        />
                      </FormItem>
                    </div>
                  ) : (
                    renderIf(
                      form_values?.adj_mode ===
                        CodiAdjModes.USE_REFERENCES.value,
                      <FormItem
                        rules={rules}
                        name="adj_document_row_id"
                        label="reference document">
                        <AdjPicker
                          current_row_id={row_id}
                          onDocumentChange={(item, option) => {
                            set_form_values({
                              ...form_values,
                              codi_document: option,
                              codi_document_row: undefined,
                            })
                            if (!item) {
                              form.setFieldsValue({
                                adj_document_row_id: undefined,
                              })
                            }
                          }}
                          onRowChange={([item, option]) => {
                            set_form_values({
                              ...form_values,
                              codi_document_row: option,
                            })
                            form.setFieldsValue({
                              adj_document_row_id: _.get(item, 'value'),
                            })
                          }}
                          codi_document={_.get(form_values, 'codi_document')}>
                          {([__, toggle]) => {
                            const { codi_document, codi_document_row } =
                              form_values ?? {}
                            return (
                              <div
                                onClick={() => toggle(true)}
                                style={{ padding: '4px 11px' }}
                                className="flex flex-col gap-1 border rounded-lg background cursor-pointer hover:shadow-out">
                                <div className="flex flex-col flex-1">
                                  {codi_document_row ? (
                                    <React.Fragment>
                                      <div className="flex flex-col gap-1">
                                        <span className="text-color-000 text-sm font-semibold">
                                          {getTitle(codi_document)}
                                        </span>
                                        <span className="font-medium text-xs text-color-200 italic">
                                          <span className="font-light text-color-400">
                                            {t('document no')}
                                            {' : '}
                                          </span>
                                          {_.get(codi_document, 'document_no')}
                                        </span>
                                        <span className="text-xs text-color-200 font-light leading-tight italic">
                                          {getTitle(codi_document_row)}
                                        </span>
                                      </div>
                                    </React.Fragment>
                                  ) : (
                                    renderAdjRow(null)
                                  )}
                                </div>
                                <div className="text-right italic text-2xs font-light text-color-400">
                                  {`( ${t('click to change')} )`}
                                </div>
                              </div>
                            )
                          }}
                        </AdjPicker>
                      </FormItem>
                    )
                  )}
                </React.Fragment>
              </div>
            </Spin>
          </div>
        </div>
        <div className="flex-1" />
        {renderActions(
          <Button
            type="primary"
            htmlType="submit"
            loading={!!isSubmitting}
            disabled={!!isSubmitting}
            className="rounded-lg no-shadow no-text-shadow font-medium">
            {t('is_rejected')}
          </Button>,
          { isSubmitting }
        )}
      </Wrapper>
    </Form>
  )
}

export default CancelCodiContent
